import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
} from "firebase/firestore";
import { db } from "../Config/Firebase";
import RefundDataViewModel from "../Assets/RefundDataViewModel";

const TopperList = () => {
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [detailsModalShow, setDetailsModalShow] = useState(false);
  const [itemToView, setItemToView] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  useEffect(() => {
    setLoading(true);

    // Query only pending, ordered by date desc
    const q = query(
      collection(db, "withdrawals"),
      where("status", "==", "pending"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        try {
          const withdrawalWithUser = await Promise.all(
            snapshot.docs.map(async (docSnap) => {
              const data = docSnap.data();

              // Fetch user info
              const userRef = doc(db, "users", data.userId);
              const userSnap = await getDoc(userRef);
              const userData = userSnap.exists() ? userSnap.data() : {};

              return {
                docid: docSnap.id,
                id: docSnap.id,
                amountRupees: data.amountRupees,
                receiveRupees: data.receiveRupees,
                method: data.method,
                bankAccount: data.bankAccount || "",
                upiId: data.upiId || "",
                status: data.status,
                createdAt: data.createdAt,
                userName: userData.name || "Unknown User",
                userEmail: userData.email || "",
                mobile: userData.mobile || "",
                profileImageUrl: userData.profileImageUrl || "",
              };
            })
          );

          setTopUsers(withdrawalWithUser);
        } catch (err) {
          console.error(err);
          setError("Failed to load withdrawals");
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        console.error("Snapshot error:", err);
        setError("Failed to listen to withdrawals");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const handleViewClick = (id) => {
    setItemToView(id);
    setDetailsModalShow(true);
  };

  const cancelView = () => {
    setDetailsModalShow(false);
    setItemToView(null);
  };

  // Pagination logic
  const totalPages = Math.ceil(topUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = topUsers.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  if (loading) return <p>Loading...</p>;
  if (error) return <p className="text-red-500">{error}</p>;

  return (
    <div className="Fee-Details-Container">
      <h1 className="text-xl font-semibold mb-4">Pending Refund Requests</h1>

      {currentData.length > 0 ? (
        <div className="Student-List">
          <table className="w-full text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Requested Amount</th>
                <th className="px-4 py-2  text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {currentData.map((user) => (
                <tr key={user.id}>
                  <td className="px-4 py-2 flex flex-wrap items-center gap-2">
                    <img
                      src={user.profileImageUrl || "https://via.placeholder.com/40"}
                      alt="avatar"
                      className="w-8 h-8 rounded-full"
                    />
                    {user.userName}
                  </td>
                  <td className="px-4 py-2">₹{user.receiveRupees || 0}</td>
                  <td
                    className={`px-4 py-2 font-semibold cursor-pointer ${
                      user.status?.toLowerCase() === "successful"
                        ? "text-green-600"
                        : user.status?.toLowerCase() === "pending"
                        ? "text-yellow-500"
                        : "text-red-600"
                    }`}
                    onClick={() => handleViewClick(user.docid)}
                  >
                    {user.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Controls */}
          <div className="flex justify-center mt-4 gap-2 items-center">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage === 1}
            >
              Prev
            </button>

            <span className="ml-4 text-sm text-gray-600">
              Page {currentPage} / {totalPages || 1}
            </span>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              className="px-3 py-1 border rounded disabled:opacity-50"
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      ) : (
        <p>No users found.</p>
      )}

      {detailsModalShow && (
        <RefundDataViewModel
          user={topUsers.find((p) => p.docid === itemToView) || null}
          onClose={cancelView}
          onUpdateStatus={(userId, newStatus) => {
            updateDoc(doc(db, "withdrawals", userId), { status: newStatus })
              .then(() => console.log("Status updated successfully"))
              .catch((err) => console.error("Error updating:", err));
            setDetailsModalShow(false);
            setItemToView(null);
          }}
        />
      )}
    </div>
  );
};

export default TopperList;
